/**
 * CctvIpPrefixDialog — 맵 에디터에서 현장 CCTV IP 앞자리(prefix)를 설정한다.
 *
 * 프로젝트 생성 때 'CCTV IP 앞자리'를 빼먹으면 DXF 변환 시 CCTV IP 사이드카가
 * 만들어지지 않아 PDF 매핑(1/3 IP)에서 "서버에 저장된 CCTV IP가 없습니다"로
 * 멈춘다. 대시보드의 프로젝트 수정으로 돌아가지 않고 에디터에서 바로 고칠 수
 * 있게 한다. 저장 후에는 DXF 변환을 다시 실행해야 사이드카가 생성된다.
 */
import React, { useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material'

/** 앞 두 옥텟 형식(예: 172.19, 10.130). 도면 라벨(33.14)과 합쳐 IP가 된다. */
const PREFIX_RE = /^\d{1,3}\.\d{1,3}$/

interface CctvIpPrefixDialogProps {
  open: boolean
  /** 현재 저장된 prefix(없으면 빈 문자열) — 열릴 때마다 입력값으로 리셋. */
  initialPrefix: string
  saving: boolean
  onSave: (prefix: string) => void
  onClose: () => void
}

export const CctvIpPrefixDialog: React.FC<CctvIpPrefixDialogProps> = ({
  open,
  initialPrefix,
  saving,
  onSave,
  onClose,
}) => {
  const [value, setValue] = useState(initialPrefix)
  const [touched, setTouched] = useState(false)

  useEffect(() => {
    if (open) {
      setValue(initialPrefix)
      setTouched(false)
    }
  }, [open, initialPrefix])

  const trimmed = value.trim()
  const valid = PREFIX_RE.test(trimmed)

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>CCTV IP 앞자리 설정</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          DXF 변환 시 도면의 카메라 라벨(예: 33.14)과 합쳐 CCTV IP
          (예: 172.19.33.14)를 자동 구성하는 데 사용됩니다. 저장 후{' '}
          <strong>DXF 변환을 다시 실행</strong>해야 CCTV IP가 추출됩니다.
        </Alert>
        <TextField
          autoFocus
          fullWidth
          label="CCTV IP 앞자리"
          placeholder="예: 172.19"
          value={value}
          onChange={e => {
            setValue(e.target.value)
            setTouched(true)
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' && valid && !saving) onSave(trimmed)
          }}
          error={touched && !valid}
          helperText={
            touched && !valid ? '앞 두 옥텟 형식으로 입력하세요 (예: 172.19)' : ' '
          }
          disabled={saving}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          취소
        </Button>
        <Button
          variant="contained"
          onClick={() => onSave(trimmed)}
          disabled={!valid || saving}
        >
          {saving ? '저장 중...' : '저장'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CctvIpPrefixDialog
